import { Checkbox, Skeleton, RadioGroup } from '@perch-labs/shrike';
import { useMemo, useState } from 'react';
import { Button } from '~/components/button';
import { ErrorMessage } from '~/components/error-message';
import { Form } from '~/components/form';
import { Container } from '~/components/layout';
import { RadioBoxText } from '~/components/radio-box-text';
import { ScrollableTermsDialog } from '~/components/terms/scrollable-terms';
import { TermsText } from '~/components/terms/terms-text';
import { Text } from '~/components/typography';
import { MINNESOTA_LMI_FUNNEL_ID } from '~/funnels/minnesota-lmi/config';
import { PERCH_FUNNEL_ID } from '~/funnels/perch-energy/config';
import { LDClient } from '~/providers/launch-darkly';
import { LDFlags } from '~/providers/launch-darkly/flags';
import { SignupContractVersion } from '~/services/fetch-personalized-signup-contract-set-from-template';
import { ContractVersion } from '~/typings/context';
import { ContractTag, ContractType } from '~/typings/graphql';
import { IS_PERCH_ENERGY, PERCH_CREDIT_CHECK_CONSENT_KEY } from '~/utils/constants';
import { getPerchPersonalizationError } from '~/utils/contract-personalization';
import { EventName } from '~/utils/logger';
import { trackError } from '~/utils/tracking';
import { ContractErrorMessage } from '../contract-error-message';

const creditConsentNotesError = (contract: ContractVersion): string | null => {
  if (
    'internalName' in contract &&
    contract.internalName === PERCH_CREDIT_CHECK_CONSENT_KEY &&
    !('notes' in contract && contract.notes)
  ) {
    return 'Credit check consent notes are unavailable';
  }
  return null;
};

export type SolarTermsProps = {
  canSubmit: boolean; // use to track completion of other form elements outside of this component (e.g. lmi form)
  consolidateScrollableTermsText?: boolean;
  contracts: ContractVersion[];
  copy: { submitButton: string; preText?: string; postText?: string };
  error?: string;
  loading: boolean;
  pending: boolean;
  onSubmit: (payload: { contractIds: number[]; ucbOptOut?: boolean }) => void;
  originationSourceType?: string;
  inReviewScreen?: boolean;
  showPersonalizedContractPreview?: boolean;
  utilityAccountId?: number;
  planId?: number;
  funnelId?: string;
  utilityState?: string;
  paymentProvider?: string | null;
};

export const SolarTerms = ({
  canSubmit,
  contracts,
  copy = {
    submitButton: 'Looks good to me!',
    preText: 'I have read the',
    postText: '. By proceeding, I will be submitting my e-signature.',
  },
  error,
  loading,
  onSubmit,
  pending,
  originationSourceType,
  inReviewScreen,
  showPersonalizedContractPreview,
  utilityAccountId,
  consolidateScrollableTermsText,
  planId,
  funnelId,
  utilityState,
  paymentProvider,
}: SolarTermsProps) => {
  const [checked, setChecked] = useState(false);
  const [nysegChecked, setNysegChecked] = useState(false);
  const [ucbOptIn, setUcbOptIn] = useState(true);

  const handleSubmit = () => {
    onSubmit({ contractIds: contracts.map(c => c.id) });
  };

  const contractCase = useMemo(() => {
    if (loading) return 'loading';
    if (contracts.length === 0) return 'empty';
    const perchContractErrors = IS_PERCH_ENERGY
      ? contracts
          .map(contract => ({
            contractId: contract.id,
            contractType: contract.contractType,
            error:
              getPerchPersonalizationError(contract as SignupContractVersion) ||
              creditConsentNotesError(contract),
          }))
          .filter(contract => !!contract.error)
      : [];

    if (perchContractErrors.length > 0) {
      trackError(new Error('Error fetching personalized contract'), {
        extras: {
          contractErrors: perchContractErrors,
        },
      });
      return 'error';
    }
    if (contracts.find(c => c.tag === ContractTag.Nyseg)) return 'nyseg';
    if (
      funnelId === MINNESOTA_LMI_FUNNEL_ID ||
      (utilityState === 'MN' && funnelId === PERCH_FUNNEL_ID)
    )
      return 'mn-lmi';
    if (contracts.find(c => c.scrollRequired)) return 'scrollable';
    if (contracts.find(c => c.contractType === ContractType.DirectAndDisclosure))
      return 'direct-and-disclosure';
    return 'standard';
  }, [contracts, funnelId, utilityState, loading]);

  const isBillingPreferenceEnabled = () => {
    if (paymentProvider) {
      return false;
    }
    return LDClient.variation(LDFlags.ffShowBillingPreferenceForMn, false);
  };

  return (
    <>
      {contractCase === 'loading' && (
        <Container>
          <Skeleton backgroundColor="tertiary" width="100%" />
          <Skeleton backgroundColor="tertiary" width="100%" />
          <Skeleton backgroundColor="tertiary" width="100%" />
        </Container>
      )}
      {contractCase === 'error' && (
        <Container>
          <ContractErrorMessage />
          <Button mode="primary" type="submit" disabled mt={6}>
            {copy.submitButton}
          </Button>
        </Container>
      )}
      {contractCase === 'standard' && (
        <Form isValid={!!checked && canSubmit} onSubmit={handleSubmit}>
          <Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}>
            <TermsText
              contracts={contracts}
              originationSourceType={originationSourceType}
              inReviewScreen={inReviewScreen}
              utilityAccountId={utilityAccountId}
              showPersonalizedContractPreview={showPersonalizedContractPreview}
              preText={copy.preText}
              postText={copy.postText}
              planId={planId}
              screen={EventName.LegalScreen}
            />
          </Checkbox>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              type="submit"
              disabled={!(checked && canSubmit)}
              loading={pending}
            >
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'empty' && (
        <Form isValid={canSubmit} onSubmit={handleSubmit}>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button mode="primary" type="submit" disabled={!canSubmit} loading={pending}>
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'direct-and-disclosure' && (
        <Form isValid={!!checked && canSubmit} onSubmit={handleSubmit}>
          <Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}>
            <TermsText
              contracts={contracts}
              showPersonalizedContractPreview={showPersonalizedContractPreview}
              preText={copy.preText}
              postText={copy.postText}
              planId={planId}
              screen={EventName.LegalScreen}
            />
          </Checkbox>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              type="submit"
              disabled={!(checked && canSubmit)}
              loading={pending}
            >
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'nyseg' && (
        <Form isValid={!!checked && !!nysegChecked && canSubmit} onSubmit={handleSubmit}>
          <Checkbox
            margin="0 0 16px 0"
            checked={nysegChecked}
            onChange={e => setNysegChecked(e.target.checked)}
          >
            <Text textStyle="paragraph400" color="secondary">
              By checking this box, I understand and authorize Arcadia to enable electronic billing
              ("e-billing”) of my monthly electric utility billing statements.
            </Text>
          </Checkbox>
          <Checkbox checked={checked} onChange={e => setChecked(e.target.checked)}>
            <TermsText
              contracts={contracts.filter(c => c.tag !== ContractTag.Nyseg)}
              utilityAccountId={utilityAccountId}
              showPersonalizedContractPreview={showPersonalizedContractPreview}
              screen={EventName.LegalScreen}
            />
          </Checkbox>
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              type="submit"
              disabled={!(checked && nysegChecked && canSubmit)}
              loading={pending}
            >
              {copy.submitButton}
            </Button>
          </Container>
        </Form>
      )}
      {contractCase === 'mn-lmi' && (
        <>
          {isBillingPreferenceEnabled() ? (
            <>
              <Text textStyle="heading500" color="secondary" paddingBottom="16px">
                <b>Select your billing preference </b>
              </Text>
              <RadioGroup
                name="billing-preference-selection"
                options={[
                  {
                    children: (
                      <Container onClick={() => setUcbOptIn(true)}>
                        <RadioBoxText>
                          I opt in to participate in Utility Consolidated Billing. This means
                          savings from the community solar credits will be applied directly to my
                          electric utility bill (solar credits received net of subscription
                          payment). I will not receive a separate bill for my community solar
                          credits.
                        </RadioBoxText>
                      </Container>
                    ),
                    value: 'opt-in',
                  },
                  {
                    children: (
                      <Container onClick={() => setUcbOptIn(false)}>
                        <RadioBoxText>
                          No thanks, I prefer to pay two separate bills every month, one to my
                          electric utility and a separate bill for my community solar credits.
                        </RadioBoxText>
                      </Container>
                    ),
                    value: 'opt-out',
                  },
                ]}
                isChecked={(_, value) => value === (ucbOptIn ? 'opt-in' : 'opt-out')}
                onChange={value => setUcbOptIn(value === 'opt-in')}
                width="initial"
              />
            </>
          ) : null}
          {!ucbOptIn ? (
            <Container mt={6}>
              <ErrorMessage error={error} mb={3} />
              <Button
                mode="primary"
                disabled={false}
                loading={pending}
                testId="ucb-opt-out-scrollable-terms-button"
                onClick={() => {
                  onSubmit({ contractIds: [], ucbOptOut: !ucbOptIn });
                }}
              >
                View terms
              </Button>
            </Container>
          ) : null}
          <ScrollableTermsDialog
            contracts={contracts.filter(c => c.contractType !== ContractType.Lmi)}
            onSubmit={handleSubmit}
            pending={pending}
            fetchPersonalizedContractForContractType={['disclosure', 'data_consent']}
            utilityAccountId={utilityAccountId!}
            showPersonalizedContractPreview={showPersonalizedContractPreview}
          >
            <Container mt={6}>
              {ucbOptIn ? (
                <>
                  <ErrorMessage error={error} mb={3} />
                  <Button
                    mode="primary"
                    disabled={!canSubmit}
                    loading={pending}
                    testId="view-scrollable-terms-button"
                  >
                    View terms
                  </Button>{' '}
                </>
              ) : null}
            </Container>
          </ScrollableTermsDialog>
        </>
      )}
      {contractCase === 'scrollable' && (
        <ScrollableTermsDialog
          contracts={contracts}
          onSubmit={handleSubmit}
          pending={pending}
          consolidateTermsText={consolidateScrollableTermsText}
          fetchPersonalizedContractForContractType={['direct', 'disclosure']}
          utilityAccountId={utilityAccountId}
          showPersonalizedContractPreview={showPersonalizedContractPreview}
        >
          <Container mt={6}>
            <ErrorMessage error={error} mb={3} />
            <Button
              mode="primary"
              disabled={!canSubmit}
              loading={pending}
              testId="view-scrollable-terms-button"
            >
              View terms
            </Button>
          </Container>
        </ScrollableTermsDialog>
      )}
    </>
  );
};
